import { useEffect, useMemo, useRef, useState } from "react";
import { FaArrowRight, FaClock } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { saveUserData } from "../utils/authStorage";
import { resendOtpApi, verifyOtpApi } from "../api/api";
import logo from "../resources/qr-icon-white.png";
import "../styles/VerifyAccount.css";

const OTP_LENGTH = 6;
const OTP_TIMER_SECONDS = 180;

const decodeJwtPayload = (token) => {
  if (!token) return null;
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch (decodeError) {
    return null;
  }
};

const normalizeUserType = (value) => {
  const type = String(value || "").toLowerCase();
  if (["admin", "super_admin", "superadmin"].includes(type)) return "admin";
  if (["org", "org-admin", "organizationadmin", "organization_admin"].includes(type)) return "org";
  if (["subordinate", "user", "sub_user", "sub-user"].includes(type)) return "subordinate";
  return type;
};

const normalizeRoles = (value) => {
  if (!Array.isArray(value)) return [];
  return value.flat().filter(Boolean);
};

const VerifyAccount = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const pendingEmail = useMemo(() => sessionStorage.getItem("pendingLoginEmail") || "", []);

  const [otpValues, setOtpValues] = useState(Array(OTP_LENGTH).fill(""));
  const [secondsLeft, setSecondsLeft] = useState(OTP_TIMER_SECONDS);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");

  const inputsRef = useRef([]);

  useEffect(() => {
    if (!pendingEmail) {
      navigate("/");
    }
  }, [navigate, pendingEmail]);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const timer = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  const formatTime = (value) => {
    const mins = Math.floor(value / 60)
      .toString()
      .padStart(2, "0");
    const secs = (value % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...otpValues];
    next[index] = digit;
    setOtpValues(next);
    setError("");

    if (digit && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otpValues[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleResend = async () => {
    if (!pendingEmail || secondsLeft > 0) return;

    setResending(true);
    setError("");
    try {
      const response = await resendOtpApi(pendingEmail);
      if (!response?.isSuccess) {
        setError(response?.message || "Unable to resend OTP. Please try again.");
        return;
      }

      setOtpValues(Array(OTP_LENGTH).fill(""));
      setSecondsLeft(OTP_TIMER_SECONDS);
      inputsRef.current[0]?.focus();
    } catch (apiError) {
      setError(apiError?.response?.data?.message || "Unable to resend OTP. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    const otp = otpValues.join("");

    if (otp.length !== OTP_LENGTH) {
      setError("Please enter complete 6-digit OTP.");
      return;
    }

    setSubmitting(true);

    try {
      const response = await verifyOtpApi(pendingEmail, otp);

      if (!response?.isSuccess) {
        setError(response?.message || "OTP verification failed.");
        return;
      }

      const token =
        response?.token ||
        response?.data?.token ||
        response?.jwt ||
        response?.data?.jwt ||
        response?.accessToken ||
        response?.data?.accessToken;

      if (!token) {
        setError("Verification succeeded but token was not returned.");
        return;
      }

      const responseData = response?.data || {};
      const userData = response?.user || responseData?.user || responseData || {};
      const profile = responseData?.profile || userData?.profile || {};
      const decodedToken = decodeJwtPayload(token);
      const decodedUser = decodedToken?.user || decodedToken || {};

      const normalizedUserType = normalizeUserType(
        decodedUser?.user_type || decodedUser?.type || userData?.user_type || userData?.type
      );

      const normalizedRoles = normalizeRoles(decodedUser?.roles).length
        ? normalizeRoles(decodedUser?.roles)
        : normalizeRoles(userData?.roles);

      const orgId = profile?.orgId ?? userData?.orgId ?? decodedUser?.orgId ?? null;

      localStorage.setItem("token", token);
      saveUserData({
        token,
        user: {
          ...userData,
          ...decodedUser,
          profile,
          orgId,
          email: userData?.email || pendingEmail,
          username: userData?.username || userData?.email || pendingEmail,
          user_type: normalizedUserType,
          roles: normalizedRoles,
        },
      });
      login();
      sessionStorage.removeItem("pendingLoginEmail");
      navigate("/dashboard");
    } catch (apiError) {
      setError(apiError?.response?.data?.message || "Unable to verify OTP. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="verify-container">
      <header className="verify-brandtop">QRiva</header>

      <section className="verify-leftpanel">
        <div className="verify-emblem-wrap">
          <div className="verify-emblem-frame">
            <img src={logo} alt="QRiva" className="verify-emblem" />
          </div>
        </div>
        <h1 className="verify-lefttitle">QRiva</h1>
        <p className="verify-leftsubtitle">
          Real-time transaction infrastructure for the world's leading financial institutions.
        </p>
      </section>

      <section className="verify-rightpanel">
        <div className="verify-card">
          <h2 className="verify-cardtitle">Verification Required</h2>
          <p className="verify-cardsub">
            Please enter the 6-digit code sent to your registered device to access the Admin Console.
          </p>
          <p className="verify-email">{pendingEmail}</p>

          <form className="verify-form" onSubmit={handleVerify}>
            <div className="verify-otp-row">
              {otpValues.map((digit, idx) => (
                <input
                  key={idx}
                  ref={(el) => {
                    inputsRef.current[idx] = el;
                  }}
                  className="verify-otp-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(idx, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                />
              ))}
            </div>

            <div className="verify-otp-meta">
              <div className="verify-timer">
                <FaClock /> {formatTime(secondsLeft)}
              </div>
              <button
                type="button"
                className="verify-resend"
                onClick={handleResend}
                disabled={secondsLeft > 0 || resending}
              >
                {resending ? "Resending..." : "Resend Code"}
              </button>
            </div>

            {error ? <div className="verify-error">{error}</div> : null}

            <button type="submit" className="verify-btn" disabled={submitting}>
              {submitting ? "Verifying..." : "Verify Account"}
              <FaArrowRight />
            </button>

            <div className="verify-security">
              Secured by <span>QRiva Shield</span>
            </div>
          </form>
        </div>

        <footer className="verify-footerlinks">
          <button type="button">Privacy Policy</button>
          <button type="button">Security Center</button>
          <button type="button">Contact Support</button>
        </footer>
      </section>
    </div>
  );
};

export default VerifyAccount;
